<template>
  <div class="wrapper rice-game-page">
    <SiteHeader
      :user="headerUser"
      :show-chrome="true"
      :nav-items="navItems"
      :active-section="activeSection"
      :is-header-scrolled="isHeaderScrolled"
      :is-menu-open="isMenuOpen"
      :upload-disabled="riceHeaderUploadDisabled"
      :show-guest-upload-in-mobile="false"
      :show-guest-upload-on-desktop="false"
      @toggle-menu="toggleMenu"
      @close-menu="closeMenu"
      @nav-click="handleHeaderNavClick"
      @logo-click="redirectToHomePage"
      @open-login="openCommonLoginPopup"
      @guest-upload="openCommonLoginPopup"
      @auth-upload="goUploadCheckForNextKazan"
      @account="redirectToPrivatePage"
      @logout="logout"
    />

    <main class="main rice-game-main">
      <div v-if="ricePreviewMode" class="rice-game-dev-preview-bar" aria-label="Временный режим превью">
        <span class="rice-game-dev-preview-bar__label">DEV: <code>rice_preview=1</code></span>
        <span v-if="$route.query.rice_pack" class="rice-game-dev-preview-bar__meta"><code>rice_pack={{ $route.query.rice_pack }}</code></span>
        <button type="button" class="rice-game-dev-preview-bar__btn" @click="ricePreviewGoIntro">Интро</button>
        <button type="button" class="rice-game-dev-preview-bar__btn" @click="ricePreviewGoPlay">Игра</button>
        <button type="button" class="rice-game-dev-preview-bar__btn" @click="ricePreviewGoResult">Результат</button>
      </div>
      <div class="container">
        <div v-if="screenPhase === 'loading'" class="rice-game-loading">
          <p>Загрузка…</p>
        </div>

        <div v-else-if="screenPhase === 'unavailable'" class="rice-game-gate container container--small">
          <p class="rice-game-gate__text">Этот казан сейчас недоступен для игры.</p>
          <button type="button" class="button button-main" @click="goProfile">В личный кабинет</button>
        </div>

        <!-- 1) Правила → 2) после «Приготовить плов» и startGameSession — экран готовки (play). -->
        <section
            v-else-if="screenPhase === 'intro'"
            class="rice-game-panel rice-game-panel--intro"
        >
          <div class="rice-game-panel__inner">
            <h1 class="rice-game-title">Соберите правильный плов</h1>

            <div class="rice-game-steps rice-game-steps--desktop" aria-label="Как играть">
              <template v-for="(step, idx) in riceIntroRuleSlides" :key="'d-' + step.key">
                <div class="rice-game-step">
                  <div class="rice-game-step__media">
                    <img :src="step.img1x" :srcset="step.imgSrcset" alt="">
                  </div>
                  <div class="rice-game-step__text">{{ step.text }}</div>
                </div>
                <span
                  v-if="idx < riceIntroRuleSlides.length - 1"
                  class="rice-game-step__arrow"
                  aria-hidden="true"
                >
                  <svg width="101" height="124" viewBox="0 0 101 124" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.4759 102.115C40.821 98.2253 68.7027 98.0586 97.1941 99.8843C101.9 100.188 101.828 92.875 97.1978 92.4841C68.8817 89.9563 39.3403 92.7412 11.6264 99.0808C9.62295 99.5355 10.4854 102.419 12.4027 102.158L12.4759 102.115Z" fill="white"/>
                    <path d="M57.3138 72.7464C70.0809 82.0791 83.9038 89.8482 96.7844 99.0854L96.7832 93.9973C90.9625 97.9436 85.166 101.933 79.4525 106.044C73.739 110.155 68.6812 115.476 62.5338 118.745C59.1633 120.534 62.8903 125.919 65.7794 123.032C70.7504 118.076 77.5302 114.79 83.2216 110.763C88.6993 106.876 94.1589 102.957 99.6531 99.0754C100.072 98.8028 100.415 98.4253 100.651 97.9788C100.887 97.5324 101.007 97.0318 101 96.5246C100.993 96.0174 100.859 95.5204 100.611 95.081C100.363 94.6415 100.009 94.2741 99.5831 94.0136C86.5722 85.1384 72.9722 75.9121 58.3335 70.1066C56.715 69.4647 56.0511 71.8947 57.2615 72.7773L57.3138 72.7464Z" fill="white"/>
                    <path d="M0.399778 103.436C2.21233 106.643 7.04706 103.792 5.23451 100.584C3.42196 97.3772 -1.41277 100.229 0.399778 103.436Z" fill="white"/>
                  </svg>
                </span>
              </template>
            </div>

            <div class="rice-game-intro-rules-swiper-wrap" aria-label="Как играть, листайте шаги">
              <Swiper
                class="rice-game-intro-rules-swiper"
                :modules="riceIntroSwiperModules"
                :slides-per-view="1"
                :space-between="12"
                :pagination="riceIntroRulesPagination"
              >
                <SwiperSlide v-for="step in riceIntroRuleSlides" :key="'m-' + step.key">
                  <div class="rice-game-step rice-game-step--mobile">
                    <div class="rice-game-step__media">
                      <img :src="step.img1x" :srcset="step.imgSrcset" alt="">
                    </div>
                    <div class="rice-game-step__text">{{ step.text }}</div>
                  </div>
                </SwiperSlide>
              </Swiper>
              <div id="rice-game-intro-rules-pagination" class="rice-game-intro-rules-pagination"></div>
            </div>

            <p v-if="riceGameError" class="rice-game-error">{{ popupErrorMessage(riceGameError) }}</p>
            <button
                type="button"
                class="button rice-game-cta"
                :disabled="riceGameFinalizing"
                @click="startCook"
            >
              {{ riceGameFinalizing ? 'Загрузка…' : 'Приготовить плов' }}
            </button>
          </div>
        </section>

        <section
            v-else-if="screenPhase === 'play'"
            class="rice-game-panel rice-game-panel--play"
            :style="playPanelStyle"
        >
          <div class="rice-game-panel__inner">
            <h1 class="rice-game-title">Соберите правильный плов</h1>
            <p class="rice-game-subtitle">Готовим плов «{{ activeRiceGame && activeRiceGame.title }}»</p>
            <div
              class="rice-game-scene rice-game-scene--play"
              @click="onKazanClick"
            >
              <img
                  v-if="currentFrameSrc"
                  :src="currentFrameSrc"
                  alt=""
                  class="rice-game-frame-img"
                  draggable="false"
              >
              <button
                  type="button"
                  class="rice-game-kazan"
                  :disabled="riceGameFinalizing"
                  aria-label="Добавить ингредиент в казан"
                  @click.stop="onKazanClick"
              >
                <span
                  ref="riceKazanHand"
                  class="rice-game-kazan__hand"
                  :class="{ 'rice-game-kazan__hand--idle': frameStep === 0 && !riceGameFinalizing }"
                  aria-hidden="true"
                >
                  <svg width="114" height="149" viewBox="0 0 114 149" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g filter="url(#filter0_d_11849_38993)">
                    <path d="M50.2422 42.8848C51.5386 42.4306 52.9363 42.3461 54.2783 42.6426C55.636 42.9426 56.8817 43.6197 57.8701 44.5967L58.1191 44.8555C58.6844 45.4726 59.1389 46.1839 59.4609 46.958C59.828 47.8404 60.0162 48.7866 60.0137 49.7422V60.3057C60.972 59.8191 62.0401 59.5576 63.1348 59.5576C64.9553 59.5577 66.7031 60.2777 67.9932 61.5615C69.0758 62.6391 69.7593 64.0409 69.9521 65.5391C70.1186 65.4525 70.2883 65.3717 70.4619 65.2988C71.3075 64.9437 72.2155 64.7608 73.1328 64.7607C74.0502 64.7607 74.9589 64.9437 75.8047 65.2988C76.6504 65.654 77.4172 66.1737 78.0586 66.8291C78.7 67.4845 79.2036 68.262 79.5391 69.1152C79.661 69.4253 79.7588 69.7434 79.834 70.0664C80.837 69.52 81.9693 69.2256 83.1318 69.2256C84.9526 69.2256 86.701 69.9454 87.9912 71.2295C89.2815 72.5139 90.0078 74.258 90.0078 76.0781V99.1787L90.0039 99.5928C89.9626 101.661 89.6153 103.713 88.9736 105.683C85.9319 115.025 76.5584 121.68 65.6514 121.68C56.52 121.68 48.4985 117.031 44.3105 110.061L44.3096 110.058C41.2392 104.927 37.8271 100.083 33.7617 95.8232L25.4756 87.1416V87.1406C24.1372 85.7979 23.362 83.9955 23.3145 82.0996C23.2665 80.1855 23.9634 78.3279 25.2568 76.915C26.55 75.5027 28.3396 74.6432 30.251 74.5137C32.1414 74.3857 34.0091 74.9822 35.4746 76.1797H35.4756L45.4277 83.9023V49.6973C45.4278 48.2145 45.9766 46.7521 46.7881 45.5801C47.5897 44.4224 48.7619 43.3858 50.1963 42.9004C50.2082 42.8961 50.2205 42.8919 50.2324 42.8877C50.2357 42.8866 50.2389 42.8849 50.2422 42.8838V42.8848Z" fill="white" fill-opacity="0.4" stroke="white" stroke-width="3.75508"/>
                    <path d="M48.082 18.3773H56.2688L52.1754 31.5922L48.082 18.3773Z" fill="white"/>
                    <path d="M52.1667 32.1678C52.0413 32.1688 51.919 32.1296 51.8181 32.056C51.7173 31.9825 51.6434 31.8786 51.6076 31.7601L47.5142 18.5452C47.4886 18.4592 47.4837 18.3684 47.4996 18.2801C47.5156 18.1918 47.5522 18.1083 47.6063 18.0363C47.6605 17.9643 47.7308 17.9056 47.8119 17.8649C47.8929 17.8242 47.9824 17.8026 48.0733 17.8018H56.2552C56.3465 17.8019 56.4365 17.8229 56.5182 17.8633C56.5998 17.9037 56.6708 17.9623 56.7255 18.0344C56.7802 18.1065 56.8172 18.1903 56.8334 18.2789C56.8496 18.3676 56.8448 18.4588 56.8191 18.5452L52.7258 31.7409C52.6935 31.863 52.6211 31.9712 52.52 32.0484C52.4188 32.1257 52.2946 32.1677 52.1667 32.1678ZM48.8657 18.9578L52.1667 29.616L55.4676 18.9578H48.8657Z" fill="white"/>
                    <path d="M69.6487 23.6492L75.4338 29.3572L63.071 35.8472L69.6487 23.6492Z" fill="white"/>
                    <path d="M63.0697 36.4216C62.993 36.4218 62.917 36.4071 62.8461 36.3783C62.7752 36.3495 62.7108 36.3071 62.6564 36.2537C62.5681 36.1669 62.5098 36.0548 62.4897 35.9334C62.4697 35.812 62.4889 35.6874 62.5446 35.5774L69.1223 23.3795C69.165 23.2994 69.2266 23.2305 69.3019 23.1788C69.3772 23.127 69.464 23.0939 69.5549 23.0821C69.6432 23.0709 69.7329 23.0797 69.8172 23.108C69.9014 23.1363 69.978 23.1832 70.0411 23.2451L75.8262 28.9532C75.8908 29.017 75.9393 29.0949 75.9678 29.1805C75.9963 29.2662 76.004 29.3573 75.9903 29.4464C75.9766 29.5356 75.9418 29.6203 75.8888 29.6937C75.8358 29.7672 75.7661 29.8274 75.6853 29.8693L63.3225 36.3593C63.2438 36.3978 63.1575 36.4191 63.0697 36.4216ZM69.7883 24.6074L64.4892 34.4454L74.465 29.2122L69.7883 24.6074Z" fill="white"/>
                    <path d="M81.1237 42.4181V50.4909L67.7303 46.4569L81.1237 42.4181Z" fill="white"/>
                    <path d="M81.1266 51.0705C81.0686 51.071 81.011 51.0613 80.9565 51.0417L67.5631 47.0077C67.4427 46.9723 67.3371 46.8995 67.262 46.8001C67.1869 46.7007 67.1463 46.5801 67.1463 46.4561C67.1463 46.3321 67.1869 46.2115 67.262 46.1121C67.3371 46.0127 67.4427 45.9399 67.5631 45.9045L80.9565 41.8657C81.0438 41.8394 81.1361 41.8337 81.226 41.8491C81.3159 41.8644 81.401 41.9004 81.4743 41.954C81.5476 42.0076 81.607 42.0775 81.648 42.158C81.6889 42.2384 81.7102 42.3273 81.71 42.4173V50.4901C81.713 50.5832 81.6928 50.6755 81.6513 50.7591C81.6098 50.8428 81.5482 50.9151 81.4718 50.9698C81.3709 51.0393 81.2497 51.0747 81.1266 51.0705ZM69.7362 46.4561L80.5384 49.713V43.1992L69.7362 46.4561Z" fill="white"/>
                    <path d="M23.7215 50.9811V42.9082L37.11 46.9423L23.7215 50.9811Z" fill="white"/>
                    <path d="M23.7132 51.5571C23.5593 51.5547 23.4124 51.4932 23.3035 51.3858C23.1947 51.2784 23.1324 51.1334 23.1299 50.9815V42.9039C23.1307 42.8142 23.1526 42.726 23.1939 42.646C23.2351 42.5661 23.2945 42.4967 23.3676 42.4432C23.4406 42.3898 23.5252 42.3537 23.6147 42.338C23.7042 42.3222 23.7961 42.3271 23.8834 42.3523L37.2768 46.3911C37.3972 46.4265 37.5028 46.4994 37.5779 46.5987C37.653 46.6981 37.6936 46.8188 37.6936 46.9427C37.6936 47.0667 37.653 47.1874 37.5779 47.2867C37.5028 47.3861 37.3972 47.459 37.2768 47.4944L23.8834 51.5332C23.828 51.5485 23.7708 51.5566 23.7132 51.5571ZM24.3015 43.6858V50.1997L35.1038 46.9427L24.3015 43.6858Z" fill="white"/>
                    <path d="M29.0633 29.7025L34.8485 23.9897L41.4261 36.1876L29.0633 29.7025Z" fill="white"/>
                    <path d="M41.4226 36.7667C41.3255 36.7666 41.23 36.7419 41.1455 36.6948L28.7827 30.2096C28.7021 30.1666 28.6328 30.1057 28.5803 30.0316C28.5279 29.9575 28.4936 29.8723 28.4804 29.7829C28.4672 29.6934 28.4753 29.6021 28.5041 29.5163C28.533 29.4305 28.5818 29.3526 28.6466 29.2887L34.4318 23.5806C34.4964 23.5169 34.5753 23.469 34.6621 23.4409C34.749 23.4128 34.8413 23.4052 34.9316 23.4187C35.022 23.4323 35.1078 23.4666 35.1823 23.5189C35.2568 23.5711 35.3177 23.6399 35.3603 23.7197L41.9379 35.9177C41.9964 36.0264 42.0181 36.1507 41.9997 36.2724C41.9813 36.3942 41.9239 36.5069 41.8358 36.594C41.7266 36.7032 41.5781 36.7653 41.4226 36.7667ZM30.0321 29.5717L40.003 34.8049L34.6991 24.9621L30.0321 29.5717Z" fill="white"/>
                    </g>
                    <defs>
                    <filter id="filter0_d_11849_38993" x="-8.39233e-05" y="9.58443e-05" width="113.32" height="148.624" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                    <feOffset dy="3.63299"/>
                    <feGaussianBlur stdDeviation="10.7173"/>
                    <feComposite in2="hardAlpha" operator="out"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.63 0"/>
                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_11849_38993"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_11849_38993" result="shape"/>
                    </filter>
                    </defs>
                  </svg>

                </span>
              </button>
            </div>
            <p class="rice-game-hint">{{ riceHintText }}</p>
            <div
              v-if="activePlayFrames.length > 1"
              class="rice-game-bar"
              :class="{ 'rice-game-bar--concealed': !ricePlayProgressBarVisible }"
              aria-hidden="true"
            >
              <div class="rice-game-bar__fill" :style="{ width: frameProgressPercent + '%' }" />
            </div>
          </div>
        </section>

        <template v-else-if="screenPhase === 'result'">
          <section
              class="rice-game-panel rice-game-panel--play rice-game-panel--play-result"
              :style="playPanelStyle"
          >
            <div class="rice-game-panel__inner">
              <h1 class="rice-game-title">Соберите правильный плов</h1>
              <p class="rice-game-subtitle rice-game-subtitle--success">{{ resultSubtitleSuccess }}</p>
              <div class="rice-game-scene rice-game-scene--play">
                <img
                    v-if="resultSceneFrameSrc"
                    :src="resultSceneFrameSrc"
                    alt=""
                    class="rice-game-frame-img"
                    draggable="false"
                >
              </div>
              <p class="rice-game-hint">{{ resultPanelHintText }}</p>
              <div
                v-if="!riceResultProgressSlotDismissed && activePlayFrames.length > 1"
                class="rice-game-bar rice-game-bar--concealed rice-game-bar--result-spacer"
                aria-hidden="true"
              >
                <div class="rice-game-bar__fill" style="width: 100%" />
              </div>
              <div class="rice-game-scroll-hint-wrap">
                <button
                    type="button"
                    class="rice-game-scroll-hint rice-game-scroll-hint--action rice-game-scroll-hint--as-progress"
                    aria-controls="rice-game-result-facts"
                    @click="scrollToResultFacts"
                >
                  Интересные факты
                </button>
              </div>
            </div>
          </section>

          <div class="rice-game-result-below">
            <div class="rice-game-result-board">
              <!-- ЗАГОТОВКА: левая колонка — свой HTML под каждую игру (ниже дефолтный блок, можно заменить целиком). -->
              <div class="rice-game-result-board__col rice-game-result-board__col--left">
                <div class="rice-game-result-slot rice-game-result-slot--left-default">
                  <div class="rice-game-result-pack-rice">
                    Рис
                    <br><span>{{ resultRiceType }}</span>
                  </div>
                  <div class="rice-game-result-pack-plov">
                    для плова
                  </div>
<!--                  resultTitle-->
                  <div v-if="resultPackApiTitle" class="rice-game-result-pack-title">{{ resultPackApiTitle }}</div>
                  <a
                      v-if="hasResultReceipt"
                      class="button rice-game-download"
                      role="button"
                      :href="resultReceiptPdfHref"
                      :download="resultReceiptDownloadName"
                      rel="noopener noreferrer"
                      @click.prevent="onResultReceiptPdfClick"
                  >
                    Скачать рецепт
                    <span class="rice-game-download__icon" aria-hidden="true">
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M33.4446 24.5505C33.8023 24.5506 34.1486 24.6769 34.4177 24.9023C34.6863 25.1277 34.8541 25.4341 34.8934 25.7528C34.9568 26.2726 35.0194 26.7927 35.0779 27.3125C35.0853 27.3398 35.0928 27.3675 35.0995 27.3947C35.1497 27.5972 35.2511 27.7847 35.3518 27.9728C35.3749 28.0159 35.3985 28.0592 35.421 28.1025C35.5924 28.4326 35.5214 29.0698 35.483 29.4201C35.4823 29.4265 35.4822 29.4338 35.4815 29.4402C35.4725 29.5231 35.4697 29.6021 35.4671 29.694C35.4624 29.8638 35.4567 30.0798 35.4037 30.4551C35.3881 30.6429 35.3587 30.8533 35.3042 31.085C35.2698 31.387 35.2053 31.6862 35.1125 31.9759C34.8723 32.7287 34.44 33.4188 33.8728 33.9638C32.9384 34.8706 31.6324 35.3566 30.3641 35.2972C29.6587 35.2482 28.9533 35.189 28.2479 35.1271C28.2234 35.128 28.1981 35.1291 28.1729 35.13C27.6848 35.1477 27.0786 35.1695 26.6982 35.1069C26.2151 35.0274 25.8867 34.9544 25.6171 34.8907C24.0713 34.7534 22.5255 34.6301 20.9796 34.5793C20.4954 34.5634 20.0109 34.5548 19.5266 34.5548C19.0804 34.5548 18.6335 34.5605 18.1874 34.5707C17.9275 34.608 17.6798 34.6521 17.4508 34.6932C17.1291 34.7509 16.8426 34.8019 16.606 34.8186C16.0357 34.8589 15.3883 34.7952 14.9079 34.748C14.8296 34.7403 14.7557 34.7327 14.6873 34.7264C14.3059 34.7498 13.9242 34.7757 13.5427 34.8013C13.5173 34.7871 13.491 34.7719 13.4649 34.7566C13.1393 34.5668 12.7384 34.5686 12.1084 34.5721C11.8343 34.5737 11.5165 34.5758 11.1426 34.562C10.9013 34.5532 10.7768 34.4779 10.6755 34.4164C10.6458 34.3984 10.6173 34.3811 10.589 34.3674C10.4925 34.3213 10.3906 34.3714 10.2705 34.4309C10.1183 34.5062 9.93604 34.5967 9.69816 34.5246C9.68865 34.5225 9.67894 34.5198 9.66933 34.5174C8.61064 34.2131 8.19872 34.1321 7.79101 34.0993C7.67695 34.0851 7.57148 34.0684 7.47387 34.0316C7.21143 33.9346 6.92913 33.6728 6.75166 33.4016C6.71326 33.3439 6.67909 33.2861 6.64931 33.2301C6.51117 32.9824 6.13533 32.5859 5.87808 32.2267C5.77917 32.0935 5.69332 31.8873 5.62005 31.6372C5.54381 31.3776 5.48098 31.0715 5.42112 30.7376C5.40342 30.639 5.39064 30.5449 5.38075 30.4551C5.30595 29.9905 5.2665 29.721 5.23372 29.4994C5.22314 29.4278 5.21349 29.3612 5.20344 29.2947C5.17168 29.0842 5.14479 28.8224 5.11839 28.558C5.08683 28.242 5.05448 27.9214 5.01172 27.6772C5.05703 27.0441 5.11292 26.4104 5.16885 25.7773C5.19789 25.452 5.36197 25.1395 5.63158 24.9095C5.90135 24.6797 6.2531 24.5506 6.61759 24.5505C6.98207 24.5505 7.33381 24.6797 7.60361 24.9095C7.87322 25.1395 8.0373 25.452 8.06634 25.7773C8.12421 26.4325 8.18168 27.0883 8.22779 27.7436C8.28394 28.5416 8.32293 29.3399 8.32293 30.138C8.32293 30.2437 8.32355 30.3494 8.32293 30.4551C8.32025 30.6662 8.36002 30.8783 8.44402 31.0764C8.45468 31.1015 8.46659 31.1267 8.47862 31.1514C8.58778 31.2381 8.70215 31.3032 8.80297 31.3503C8.87628 31.3847 8.94313 31.4094 8.99613 31.4325C9.09911 31.4766 9.14479 31.554 9.19651 31.6213C9.23144 31.6669 9.26873 31.7051 9.31471 31.715C9.33924 31.7202 9.37325 31.7256 9.41418 31.728C9.4909 31.7326 9.5906 31.7297 9.69816 31.7236C10.0001 31.7165 10.3376 31.7248 10.6582 31.7727C10.9184 31.8115 11.2216 31.8883 11.5102 31.9615C11.9226 32.0661 12.3078 32.163 12.502 32.133C12.8427 32.0806 13.2335 32.1254 13.5788 32.1648C13.9085 32.2024 14.1975 32.2356 14.3615 32.1748C14.5575 32.1023 14.7244 32.1325 14.9728 32.1763C15.1451 32.2067 15.3571 32.2435 15.6445 32.2585C15.976 32.2757 16.2106 32.3262 16.41 32.3695C16.6906 32.4303 16.9026 32.4765 17.2172 32.3925C17.5668 32.2991 17.9327 32.2392 18.2205 32.2685C18.3517 32.282 18.5027 32.2792 18.6588 32.2758C18.8654 32.2712 19.0831 32.2653 19.2815 32.2988C19.4449 32.3265 19.58 32.3977 19.6996 32.4603C19.7369 32.4798 19.773 32.4987 19.8077 32.5151C19.9378 32.5142 20.0682 32.5128 20.1983 32.5107C20.2194 32.4994 20.2416 32.4862 20.2646 32.4732C20.3807 32.408 20.5235 32.3287 20.7 32.2887C20.9796 32.2256 21.2254 32.2641 21.4842 32.306C21.6858 32.3387 21.8967 32.373 22.1372 32.3608C22.3009 32.3524 22.4873 32.3245 22.672 32.2959C22.9466 32.2534 23.2186 32.2104 23.4072 32.2368C23.4778 32.2468 23.5389 32.2921 23.5989 32.3363C23.603 32.3393 23.6079 32.3434 23.6119 32.3464C24.0714 32.3125 24.532 32.2747 24.9915 32.2354C25.0311 32.224 25.0714 32.2133 25.1111 32.2022C25.3058 32.1481 25.4951 32.0953 25.6185 32.0206C25.7523 31.9396 26.0887 31.8915 26.4431 31.8419C26.675 31.8093 26.9147 31.7758 27.1105 31.7309C27.225 31.7046 27.3346 31.7499 27.4464 31.7972C27.5504 31.8411 27.6569 31.8874 27.7736 31.8765C27.8323 31.8708 27.8963 31.8506 27.9639 31.8303C28.0231 31.8126 28.0858 31.7943 28.1513 31.7828C28.2125 31.772 28.3499 31.7971 28.5247 31.8289C28.6325 31.8485 28.7551 31.8698 28.8836 31.888C29.2238 31.8595 29.5641 31.8327 29.9042 31.8073C29.975 31.7672 30.0371 31.7153 30.0859 31.6487C30.1207 31.6008 30.1505 31.5428 30.181 31.4844C30.2226 31.4045 30.2658 31.3209 30.3251 31.2551C30.3374 31.2416 30.3511 31.231 30.3641 31.222C30.3735 31.197 30.3826 31.1893 30.39 31.2047C30.3946 31.2144 30.399 31.2328 30.403 31.2494C30.4076 31.2686 30.4118 31.2862 30.416 31.2854C30.4188 31.2837 30.4208 31.2698 30.4232 31.2537C30.4272 31.227 30.4316 31.1931 30.4419 31.1903C30.4815 31.1816 30.5285 31.1842 30.576 31.1816C30.6283 31.179 30.6812 31.1689 30.7129 31.1413C30.7477 31.1107 30.8285 31.1222 30.9176 31.1139C30.9482 31.1112 30.9796 31.1071 31.0084 31.0995C31.0373 31.0918 31.0639 31.0812 31.0848 31.0677C31.1263 31.0409 31.1339 30.982 31.1339 30.9294C31.1344 30.8654 31.1258 30.811 31.1829 30.7881C31.1946 30.7834 31.2507 30.7937 31.3213 30.8025C31.3711 30.8087 31.4285 30.8143 31.4827 30.814C31.5051 30.7441 31.5228 30.6719 31.536 30.5992C31.5291 30.5958 31.5217 30.5926 31.5144 30.5892L31.2304 30.4551C31.1945 30.3644 31.1909 30.2619 31.2477 30.1466C31.266 30.1096 31.2922 30.079 31.3213 30.0529L31.4942 29.9232C31.5162 29.9022 31.5318 29.8792 31.5375 29.8525C31.5502 29.7913 31.5139 29.718 31.4741 29.6392C31.4181 29.5285 31.3553 29.4043 31.4135 29.2802C31.4174 29.272 31.4223 29.2635 31.4265 29.2543C31.4679 29.1643 31.5295 29.0307 31.6528 28.8982C31.6857 28.5052 31.7232 28.1121 31.7652 27.7191C31.8354 27.0637 31.916 26.408 31.9959 25.7528C32.0352 25.434 32.2028 25.1277 32.4716 24.9023C32.7407 24.6769 33.0869 24.5505 33.4446 24.5505ZM19.9028 6.83691C20.2673 6.83692 20.619 6.89222 20.8888 6.98972C21.1584 7.0873 21.3225 7.21932 21.3516 7.35731C21.4094 7.63532 21.4669 7.91392 21.513 8.19196C21.5691 8.53027 21.6081 8.86849 21.6082 9.2068C21.6082 9.44373 21.5993 9.682 21.5837 9.91893C21.5821 9.94293 21.5811 9.96699 21.5793 9.991C21.669 10.1899 21.7505 10.3887 21.7797 10.5287C21.8037 10.6443 21.7558 10.729 21.7148 10.8011C21.6866 10.8508 21.6619 10.8951 21.6658 10.9395C21.668 10.9633 21.6736 10.9957 21.6802 11.0347C21.7074 11.1951 21.7531 11.4679 21.6658 11.7165C21.6269 11.8269 21.5501 11.9559 21.477 12.0784C21.3724 12.2534 21.2755 12.4169 21.3054 12.4993C21.3578 12.6438 21.3131 12.8098 21.2737 12.9562C21.2361 13.096 21.2032 13.2182 21.2636 13.2878C21.3362 13.3709 21.306 13.4419 21.2622 13.5473C21.2318 13.6204 21.195 13.7107 21.18 13.8327C21.1628 13.9731 21.1122 14.0725 21.069 14.1571C21.0082 14.2761 20.962 14.3666 21.046 14.5001C21.1394 14.6484 21.1993 14.8033 21.1699 14.9254C21.1565 14.981 21.1593 15.0451 21.1627 15.1113C21.1673 15.1989 21.173 15.2911 21.1397 15.3752C21.1121 15.4444 21.0407 15.5018 20.9782 15.5525C20.9587 15.5682 20.9398 15.5839 20.9234 15.5986C20.9243 15.6538 20.9257 15.7092 20.9278 15.7644C20.9391 15.7733 20.9522 15.7834 20.9652 15.7932C21.0304 15.8424 21.1099 15.903 21.1498 15.9777C21.2129 16.0964 21.1745 16.2008 21.1325 16.3107C21.0997 16.3963 21.0654 16.4854 21.0777 16.5875C21.0861 16.6567 21.114 16.7357 21.1425 16.8138C21.1851 16.9305 21.2283 17.0466 21.2016 17.1266C21.1917 17.1566 21.1464 17.1818 21.1022 17.2073L21.0921 17.2131C21.126 17.4081 21.1638 17.6034 21.2031 17.7984C21.2144 17.8151 21.2253 17.8321 21.2362 17.8488C21.2903 17.9312 21.3435 18.0112 21.4179 18.0636C21.4989 18.1204 21.547 18.2636 21.5966 18.4139C21.6292 18.5124 21.6627 18.6148 21.7076 18.6979C21.7334 18.7462 21.6882 18.7921 21.6413 18.8392C21.5973 18.8834 21.5509 18.9294 21.562 18.979C21.5676 19.0039 21.5879 19.0311 21.6082 19.0597C21.6259 19.0847 21.6442 19.1114 21.6557 19.139C21.6665 19.165 21.6413 19.2234 21.6096 19.2976C21.59 19.3433 21.5687 19.3959 21.5505 19.4504C21.579 19.5945 21.6058 19.7387 21.6312 19.8828C21.6712 19.9129 21.4438 20.4811 21.5104 20.5017C21.5583 20.5166 21.6161 20.5306 21.6747 20.5435C21.7545 20.5612 21.8381 20.5789 21.9039 20.6041C21.9548 20.6237 21.9755 20.6477 21.9543 20.6632C21.9444 20.6704 21.9252 20.6754 21.9082 20.6805C21.8888 20.6863 21.8717 20.6915 21.8722 20.6978C21.8727 20.7023 21.8861 20.7065 21.9025 20.7122C21.929 20.7215 21.963 20.7343 21.9644 20.7526C21.969 20.8212 21.958 20.8996 21.9471 20.976C21.9426 21.0077 21.9389 21.0393 21.9356 21.0697C22.115 20.9695 22.2878 20.8625 22.4459 20.741C22.5426 20.6667 22.6343 20.5873 22.7198 20.5017C22.7987 20.4229 23.154 19.7974 23.2256 19.7113C23.245 19.6392 23.2574 19.5644 23.2688 19.4951C23.2849 19.3975 23.2994 19.3109 23.3294 19.2572C23.4016 19.1281 23.5599 19.0579 23.6782 19.0064C23.6976 18.998 23.7164 18.9895 23.733 18.9819C23.7837 18.8981 23.8337 18.8135 23.8829 18.7282C23.8974 18.7337 23.9124 18.7394 23.9276 18.7455C24.1195 18.8223 24.1892 18.7498 24.2981 18.6359C24.3455 18.5863 24.4009 18.5281 24.4768 18.4716C24.5257 18.436 24.6003 18.4678 24.6613 18.4932C24.6794 18.5007 24.6971 18.5073 24.7118 18.5119C24.7616 18.5276 24.7441 18.4743 24.7233 18.411C24.6962 18.3286 24.6633 18.2297 24.7666 18.2453C25.0958 18.2949 25.0291 18.1037 24.9785 17.9598C24.9637 17.9179 24.9499 17.8801 24.9482 17.8532C24.9429 17.7656 25.0484 17.7522 25.1558 17.771C25.2405 17.7854 25.2607 17.6666 25.2509 17.5504C25.2472 17.5064 25.2742 17.4779 25.3057 17.4452C25.3382 17.4115 25.3765 17.3729 25.3908 17.3054C25.4269 17.134 25.449 17.0458 25.4672 16.9753C25.4723 16.9552 25.4769 16.9364 25.4816 16.9176C25.4963 16.8579 25.5241 16.7927 25.5522 16.7273C25.5857 16.6492 25.62 16.57 25.6329 16.4967C25.7769 16.4168 25.9284 16.3441 26.0798 16.2718C26.1578 16.2349 26.3288 16.2951 26.5598 16.4448C26.7912 16.5949 27.0632 16.8213 27.321 17.0791C27.5788 17.3369 27.8051 17.6087 27.9553 17.8402C28.1052 18.0714 28.1652 18.2422 28.1282 18.3202C28.0533 18.477 27.9794 18.6345 27.8962 18.7829C27.7948 18.9636 27.6811 19.132 27.5401 19.2731C27.4414 19.3717 27.3355 19.4637 27.2258 19.5513C27.2147 19.5602 27.2039 19.5699 27.1927 19.5787C27.1732 19.7251 27.1468 19.8659 27.1091 19.9448C27.0778 20.0096 27.0094 20.0115 26.9505 20.0126C26.9102 20.0133 26.8741 20.0133 26.8582 20.0342C26.8498 20.0457 26.8397 20.0636 26.828 20.0847C26.7803 20.1708 26.6995 20.3168 26.5339 20.3586C26.4604 20.377 26.3526 20.3766 26.2499 20.3759C26.1035 20.3748 25.9666 20.3741 25.953 20.4292C25.9299 20.5265 25.8299 20.5647 25.7411 20.5978C25.6563 20.6295 25.5818 20.6574 25.5955 20.729C25.6122 20.815 25.5608 20.8242 25.4859 20.8371C25.4341 20.8461 25.3711 20.8577 25.31 20.8977C25.2392 20.9442 25.1611 20.9492 25.0952 20.9539C25.0027 20.9605 24.9329 20.9658 24.9367 21.0808C24.941 21.2086 24.9189 21.3158 24.8473 21.346C24.8147 21.3596 24.7902 21.3882 24.7651 21.4181C24.7318 21.4578 24.6957 21.499 24.6368 21.5103C24.5883 21.5194 24.5147 21.4945 24.4494 21.4714C24.429 21.4642 24.4095 21.4567 24.3918 21.4512C24.3694 21.4749 24.347 21.4988 24.3255 21.5233C24.3298 21.5351 24.3348 21.5489 24.3399 21.5622C24.3655 21.6289 24.3977 21.7104 24.3947 21.7698C24.3899 21.8637 24.3186 21.8792 24.2433 21.8952C24.1847 21.9077 24.123 21.9208 24.089 21.9716C24.0661 22.0064 24.0539 22.0601 24.0415 22.1129C24.023 22.1914 24.004 22.2697 23.9521 22.2844C23.9326 22.2899 23.8902 22.269 23.8483 22.2484C23.8455 22.247 23.841 22.2454 23.8382 22.2441C23.7811 22.349 23.7272 22.4568 23.6739 22.5655C23.6749 22.5805 23.676 22.5955 23.6768 22.6102C23.6807 22.683 23.6832 22.7547 23.7142 22.8294C23.7478 22.9103 23.7216 23.0026 23.6941 23.1004C23.676 23.1644 23.658 23.2315 23.6551 23.2979C23.6529 23.3358 23.6013 23.3229 23.5485 23.3094C23.499 23.2967 23.4488 23.2837 23.436 23.3123C23.4297 23.3266 23.4308 23.3509 23.4331 23.3771C23.4353 23.4003 23.438 23.425 23.4346 23.4449C23.4311 23.4631 23.3896 23.4696 23.3366 23.478C23.3036 23.4833 23.2656 23.4898 23.2299 23.4997C23.19 23.5798 23.1494 23.6596 23.1074 23.7375C23.1232 23.7784 23.1495 23.8259 23.1881 23.8817C23.2158 23.9218 23.2515 23.9674 23.2876 24.0143C23.3366 24.0781 23.387 24.1447 23.4231 24.2017C23.4509 24.2458 23.4556 24.2702 23.4346 24.2622C23.4245 24.2582 23.4083 24.2462 23.3942 24.2363C23.3782 24.2251 23.3633 24.216 23.3611 24.219C23.3608 24.2223 23.3683 24.2333 23.3769 24.2464C23.3919 24.2691 23.4111 24.2984 23.4043 24.3069C23.3789 24.3387 23.3386 24.3635 23.2991 24.3877C23.2553 24.4144 23.2127 24.4409 23.1924 24.4756C23.1697 24.5146 23.0894 24.5223 23.0093 24.5304C22.9535 24.536 22.8975 24.5416 22.8609 24.5578C22.8251 24.5735 22.8184 24.6205 22.8118 24.6659C22.8039 24.7206 22.7956 24.7743 22.7383 24.7682C22.7263 24.7665 22.681 24.7338 22.6244 24.6933C22.584 24.6643 22.5373 24.6315 22.4933 24.6024C22.4608 24.6349 22.4263 24.6652 22.3909 24.6947C22.3951 24.7012 22.3996 24.7082 22.4039 24.7149L22.538 24.9455C22.5642 25.0095 22.5592 25.0482 22.4832 25.0234C22.4442 25.0106 22.2031 24.8686 22.1527 24.8331C22.1037 24.7985 22.0595 24.7673 22.0417 24.7682C22.0219 24.7701 22.0347 24.8097 22.0489 24.8518C22.0688 24.9107 22.091 24.9757 22.0287 24.9571C22.0244 24.9558 22.0191 24.9541 22.0143 24.9527C21.9692 24.9394 21.9026 24.9197 21.7923 24.8562C21.6995 24.9024 21.6018 24.9447 21.5026 24.9845C21.3372 25.0507 21.1645 25.1097 20.9922 25.169C20.9081 25.1974 20.9182 25.169 20.5957 25.3018C20.3819 25.3898 20.6088 25.4334 20.0764 25.641C19.7013 25.5555 19.8017 25.5211 19.5055 25.3661C19.2225 25.3003 19.2145 25.1967 19.1364 25.1598C18.9797 25.0849 18.8221 25.0109 18.6737 24.9277C18.493 24.8264 18.3246 24.7126 18.1836 24.5716C18.0849 24.473 17.993 24.3671 17.9053 24.2574C17.8965 24.2462 17.8867 24.2355 17.878 24.2242C17.7315 24.2048 17.5907 24.1784 17.5118 24.1406C17.4471 24.1094 17.4451 24.041 17.4441 23.982C17.4433 23.9417 17.4433 23.9057 17.4224 23.8898C17.411 23.8813 17.3931 23.8712 17.372 23.8595C17.2859 23.8118 17.1398 23.7311 17.0981 23.5654C17.0796 23.492 16.8244 23.4914 16.8251 23.3887C16.8262 23.2422 16.8269 23.1054 16.7718 23.0917C16.6745 23.0687 16.6363 22.9687 16.6031 22.8798C16.5715 22.7951 16.5436 22.7206 16.472 22.7342C16.386 22.7509 16.3768 22.6996 16.3638 22.6247C16.3549 22.5729 16.3433 22.5098 16.3033 22.4488C16.2568 22.3779 16.2518 22.2999 16.2471 22.234C16.2405 22.1414 16.2352 22.0716 16.1202 22.0754C15.9924 22.0797 15.8852 22.0576 15.855 21.9861C15.8413 21.9534 15.8128 21.929 15.7829 21.9039C15.7431 21.8706 15.702 21.8344 15.6906 21.7756C15.6816 21.7271 15.7065 21.6534 15.7296 21.5882C15.7368 21.5678 15.7443 21.5482 15.7497 21.5305C15.7261 21.5081 15.7022 21.4858 15.6777 21.4642C15.6659 21.4685 15.652 21.4735 15.6387 21.4786C15.5721 21.5043 15.4905 21.5364 15.4312 21.5334C15.3373 21.5286 15.3218 21.4573 15.3058 21.382C15.2933 21.3234 15.2802 21.2617 15.2293 21.2278C15.1946 21.2048 15.1408 21.1926 15.0881 21.1802C15.0095 21.1618 14.9313 21.1428 14.9165 21.0909C14.9111 21.0713 14.932 21.0289 14.9526 20.9871C14.9539 20.9843 14.9555 20.9797 14.9569 20.977C14.852 20.9198 14.7442 20.8659 14.6354 20.8126C14.6205 20.8137 14.6055 20.8147 14.5907 20.8155C14.518 20.8194 14.4463 20.822 14.3716 20.853C14.2907 20.8866 14.1984 20.8603 14.1006 20.8328C14.0366 20.8148 13.9695 20.7967 13.9031 20.7939C13.8652 20.7916 13.878 20.7401 13.8916 20.6872C13.9043 20.6377 13.9173 20.5875 13.8887 20.5748C13.8744 20.5684 13.8501 20.5695 13.8238 20.5719C13.8007 20.574 13.7759 20.5768 13.7561 20.5733C13.7379 20.5698 13.7313 20.5284 13.7229 20.4753C13.7177 20.4424 13.7112 20.4043 13.7013 20.3686C13.6212 20.3288 13.5414 20.2882 13.4635 20.2461C13.4226 20.262 13.3751 20.2882 13.3193 20.3268C13.2792 20.3546 13.2336 20.3902 13.1867 20.4263C13.1229 20.4754 13.0563 20.5258 12.9993 20.5618C12.9552 20.5896 12.9307 20.5943 12.9387 20.5733C12.9428 20.5633 12.9548 20.5471 12.9647 20.533C12.9759 20.517 12.9849 20.5021 12.982 20.4998C12.9787 20.4996 12.9677 20.5071 12.9546 20.5157C12.9319 20.5306 12.9026 20.5499 12.8941 20.5431C12.8623 20.5177 12.8375 20.4773 12.8133 20.4378C12.7865 20.394 12.7601 20.3515 12.7254 20.3312C12.6864 20.3084 12.6787 20.2282 12.6706 20.1481C12.665 20.0923 12.6593 20.0363 12.6432 19.9996C12.6275 19.9639 12.5805 19.9572 12.5351 19.9506C12.4804 19.9427 12.4267 19.9344 12.4328 19.8771C12.4345 19.865 12.4672 19.8197 12.5077 19.7632C12.5367 19.7228 12.5694 19.6761 12.5985 19.632C12.5661 19.5996 12.5358 19.5651 12.5063 19.5297C12.4998 19.5338 12.4928 19.5383 12.4861 19.5426L12.2555 19.6767C12.1915 19.7029 12.1528 19.6979 12.1776 19.6219C12.1904 19.583 12.23 19.5267 12.2655 19.4763C12.3001 19.4273 12.3314 19.3831 12.3304 19.3653C12.3286 19.3455 12.2889 19.3583 12.2468 19.3725C12.1879 19.3924 12.1229 19.4147 12.1416 19.3524C12.1429 19.3481 12.1445 19.3427 12.1459 19.3379C12.1593 19.2928 12.179 19.2262 12.2425 19.1159C12.1962 19.0232 12.1539 18.9254 12.1142 18.8262C12.0479 18.6608 11.989 18.4882 11.9297 18.3159C11.9012 18.2317 11.9657 18.0597 12.1156 17.8301C12.2661 17.5999 12.4883 17.332 12.7413 17.0791C12.9941 16.8262 13.2608 16.6038 13.4909 16.4534C13.7207 16.3033 13.8939 16.2389 13.9781 16.2675C14.1147 16.3145 14.2515 16.3611 14.3846 16.4116C14.3947 16.4112 14.4053 16.4116 14.4149 16.4116C14.4861 16.4118 14.5904 16.3745 14.6945 16.3367C14.7186 16.3279 14.743 16.319 14.7666 16.3107C14.9458 16.2481 15.0086 16.4089 15.0434 16.4981L15.0463 16.5039C15.0545 16.5246 15.0666 16.5408 15.0809 16.5587C15.1153 16.6018 15.1612 16.6609 15.1933 16.847C15.228 16.876 15.2624 16.9059 15.2942 16.9378C15.6381 17.2818 15.8811 17.7273 16.1044 18.1919C16.1093 18.1956 16.1152 18.1982 16.1202 18.202C16.2188 18.2756 16.3405 18.3676 16.3638 18.4788C16.3931 18.6205 16.3987 18.732 16.4013 18.8248C16.5774 19.1951 17.0663 19.8864 17.3037 20.1954C17.378 20.2922 17.4574 20.3838 17.5429 20.4693C17.6218 20.5482 17.7048 20.624 17.7909 20.6956C17.863 20.7151 17.9378 20.7275 18.0071 20.7389C18.1047 20.755 18.1913 20.7695 18.245 20.7994C18.2968 20.8285 18.3365 20.8732 18.3718 20.922C18.4297 20.4518 18.5154 19.9809 18.3114 19.1794C18.3105 19.1691 18.3094 19.1582 18.3085 19.1477C18.2908 18.9408 18.2693 18.6848 18.3315 18.5235C18.4111 18.3183 18.4841 18.1781 18.5478 18.0636C18.6851 17.4078 18.8084 16.7518 18.8591 16.0959C18.875 15.8904 18.8836 15.6844 18.8837 15.4789C18.8836 15.2898 18.878 15.1001 18.8678 14.911C18.8306 14.801 18.7862 14.6966 18.7453 14.5996C18.6876 14.4631 18.6366 14.3411 18.6198 14.2407C18.5796 13.9987 18.6432 13.7237 18.6905 13.5199C18.6982 13.4867 18.7058 13.4552 18.7121 13.4262C18.6887 13.2643 18.6627 13.1022 18.6371 12.9404C18.6514 12.9296 18.6666 12.9183 18.6818 12.9072C18.8715 12.7691 18.8699 12.5993 18.8664 12.3321C18.8648 12.2158 18.8627 12.0812 18.8764 11.9227C18.8853 11.8203 18.9606 11.7667 19.022 11.7237C19.04 11.7112 19.0575 11.6996 19.0711 11.6877C19.1173 11.6467 19.0672 11.6032 19.0076 11.5522C18.9302 11.4858 18.8372 11.4066 18.9211 11.3014C19.189 10.9659 19.0068 10.8178 18.8692 10.706C18.8289 10.6732 18.7929 10.6427 18.7727 10.6123C18.7071 10.5136 18.7714 10.3726 18.8606 10.2663C18.9311 10.182 18.8619 10.0135 18.7727 9.88577C18.739 9.83744 18.7374 9.77241 18.7366 9.69549C18.7358 9.61579 18.7356 9.52312 18.6977 9.42448C18.6023 9.17596 18.556 9.04357 18.5189 8.93724C18.5084 8.90687 18.4987 8.87755 18.4887 8.8493C18.457 8.76016 18.43 8.64989 18.4036 8.53793C18.372 8.40369 18.3398 8.26679 18.2969 8.16313C18.3422 7.89453 18.3982 7.62588 18.4541 7.35731C18.4831 7.21931 18.6472 7.0873 18.9168 6.98972C19.1866 6.89217 19.5382 6.83694 19.9028 6.83691Z" fill="white"/>
                      </svg>
                    </span>
                  </a>
                </div>
              </div>

              <div class="rice-game-result-board__col rice-game-result-board__col--center">
                <!-- ЗАГОТОВКА: центр — картинка пака / 3D; при кастомизации подставьте свой img или блок. -->
                <div class="rice-game-result-slot rice-game-result-slot--center-pack">
                  <img
                      v-if="resultCenterPackImageUrl"
                      :src="resultCenterPackImageUrl"
                      :alt="resultTitle"
                      class="rice-game-pack-img"
                  >
                </div>
              </div>

              <div
                  id="rice-game-result-facts"
                  ref="riceGameResultFacts"
                  class="rice-game-result-board__col rice-game-result-board__col--right"
                  tabindex="-1"
              >
                <!-- ЗАГОТОВКА: правая колонка — «А вы знали?» и текст; можно расширить разметкой под игру. -->
                <div class="rice-game-result-slot rice-game-result-slot--facts">
                  <h3 class="rice-game-fact-title">{{ resultFactsHeading }}</h3>
                  <p class="rice-game-fact-text">{{ resultFactsBody }}</p>
                  <div class="rice-game-result-actions">
                    <template v-if="riceResultAllowNextKazan">
                      <button
                          type="button"
                          class="button rice-game-cta rice-game-cta--secondary"
                          @click="goNextKazan"
                      >
                        Следующий казан
                        <svg width="35" height="21" viewBox="0 0 35 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1.08389 12.2734C11.522 10.8293 21.7894 10.7674 32.2814 11.4453C34.0144 11.5579 33.9878 8.84278 32.2828 8.69768C21.8554 7.75914 10.9767 8.79312 0.771041 11.1469C0.0332766 11.3158 0.350856 12.3865 1.05692 12.2895L1.08389 12.2734Z" fill="#01146C"/>
                          <path d="M17.5955 1.36931C22.297 4.83441 27.3873 7.71898 32.1305 11.1486L32.1301 9.25949C29.9866 10.7247 27.8521 12.2058 25.7481 13.7322C23.644 15.2587 21.7815 17.2342 19.5177 18.4481C18.2765 19.1122 19.649 21.1116 20.7129 20.0396C22.5435 18.1995 25.0402 16.9794 27.136 15.4843C29.1532 14.0412 31.1637 12.5863 33.1869 11.1449C33.3411 11.0437 33.4676 10.9036 33.5544 10.7378C33.6412 10.572 33.6854 10.3862 33.6828 10.1979C33.6803 10.0095 33.631 9.82502 33.5397 9.66185C33.4483 9.49868 33.3181 9.36227 33.1611 9.26557C28.3699 5.9703 23.3617 2.54469 17.971 0.389187C17.3749 0.150873 17.1305 1.05308 17.5762 1.38077L17.5955 1.36931Z" fill="#01146C"/>
                          <path d="M1.08389 12.2734C11.522 10.8293 21.7894 10.7674 32.2814 11.4453C34.0144 11.5579 33.9878 8.84278 32.2828 8.69768C21.8554 7.75914 10.9767 8.79312 0.771041 11.1469C0.0332766 11.3158 0.350856 12.3865 1.05692 12.2895L1.08389 12.2734Z" stroke="#01146C" stroke-width="0.7"/>
                          <path d="M17.5955 1.36931C22.297 4.83441 27.3873 7.71898 32.1305 11.1486L32.1301 9.25949C29.9866 10.7247 27.8521 12.2058 25.7481 13.7322C23.644 15.2587 21.7815 17.2342 19.5177 18.4481C18.2765 19.1122 19.649 21.1116 20.7129 20.0396C22.5435 18.1995 25.0402 16.9794 27.136 15.4843C29.1532 14.0412 31.1637 12.5863 33.1869 11.1449C33.3411 11.0437 33.4676 10.9036 33.5544 10.7378C33.6412 10.572 33.6854 10.3862 33.6828 10.1979C33.6803 10.0095 33.631 9.82502 33.5397 9.66185C33.4483 9.49868 33.3181 9.36227 33.1611 9.26557C28.3699 5.9703 23.3617 2.54469 17.971 0.389187C17.3749 0.150873 17.1305 1.05308 17.5762 1.38077L17.5955 1.36931Z" stroke="#01146C" stroke-width="0.7"/>
                        </svg>

                      </button>
                    </template>
                    <template v-else-if="resultShowUploadCheckCta">
                      <p class="rice-game-next-hint rice-game-next-hint--accent">
                        Для того чтобы открыть следующий казан, загрузите чек
                      </p>
                      <button type="button" class="button rice-game-cta rice-game-cta--secondary" @click="goUploadCheckForNextKazan">
                        Загрузить чек
                      </button>
                    </template>
                    <p v-else-if="nextKazanBlockedHint" class="rice-game-next-hint">{{ nextKazanBlockedHint }}</p>
                    <button type="button" class="button rice-game-btn-profile" @click="goProfile">
                      В профиль
                    </button>
                  </div>


                  <a
                      v-if="resultFactsUrl"
                      class="rice-game-fact-link"
                      :href="resultFactsUrl"
                      target="_blank"
                      rel="noopener noreferrer"
                  >Подробнее</a>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </main>
  </div>
</template>

<script>
/* global _txq */
import {
  getToken,
  getUserInfo,
  removeToken,
  setAuthHeader,
  fetchWonRiceGames,
  startGameSession,
  completeGameFinalRound,
  formatFeedbackSendError,
  prefixPopupServerErrorMessage,
} from '@/api';
import { state } from '@/store';
import adRiver from '@/services/adriver';
import metrikaMixin from '@/mixins/metrikaMixin';
import SiteHeader from '@/components/layout/SiteHeader.vue';
import { appRootKey } from '@/injectionKeys';
import { setRiceGamePending, mergePendingIntoWonRiceList } from '@/utils/riceGamePending';
import { resolveRiceGameAssetPack } from '@/utils/resolveRiceGameAssetPack';
import { setMobileMenuScrollLock } from '@/utils/documentScrollLock';
import { riceGamePacksById } from '@/config/riceGameAssetPacks';
import { parseWonRiceGamesResponse, normalizeWonRiceGamesListForUi } from '@/utils/wonRiceNormalize';
import {
  riceReceiptAbsoluteUrl,
  downloadRiceRecipePdf,
  resolveRiceRecipePdfPath,
  riceRecipeSafeDownloadName,
} from '@/utils/riceRecipePdfDownload';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import riceIntroRules1 from '@/assets/img/rice-game/rules-1.png';
import riceIntroRules1x2 from '@/assets/img/rice-game/rules-1@2x.png';
import riceIntroRules2 from '@/assets/img/rice-game/rules-2.png';
import riceIntroRules2x2 from '@/assets/img/rice-game/rules-2@2x.png';
import riceIntroRules3 from '@/assets/img/rice-game/rules-3.png';
import riceIntroRules3x2 from '@/assets/img/rice-game/rules-3@2x.png';
import riceIntroRules4 from '@/assets/img/rice-game/rules-4.png';
import riceIntroRules4x2 from '@/assets/img/rice-game/rules-4@2x.png';

/** CRLF/LF из API → абзацы; в шаблоне к `.rice-game-fact-text` добавлен `white-space: pre-line`. */
function riceFormatFactBodyText(s) {
  if (s == null || String(s).trim() === '') return '';
  let t = String(s).trim();
  t = t.replace(/\r\n/g, '\n\n').replace(/\r/g, '\n');
  t = t.replace(/\n{3,}/g, '\n\n');
  return t;
}

/**
 * Подсказки под казаном по порядку кадров. [0] — до первого клика; [15] — перед финальным кликом;
 * [16] — экран результата (`resultPanelHintText`). При числе кадров меньше 17 из прогресса убраны реплики 11–14.
 */
const RICE_GAME_SEQ_HINTS = [
  'Начните готовить плов - кликайте на казан',
  'Давайте добавим ингредиенты!',
  'Шеф, отлично идёт!',
  'Давайте добавим еще ингредиенты!',
  'Шеф, отлично идёт!',
  'Давайте добавим овощи!',
  'И еще немного.',
  'Давайте добавим рис',
  'Золотая середина, продолжайте!',
  'Так держать, плов набирает силу.',
  'Плов почти готов',
  'Финишная прямая',
  'Ещё немного осталось, не останавливайтесь',
  'Добавьте последние штрихи',
  'Ещё миг - и плов готов',
  'Кликните ещё раз и подаем плов!',
  'Плов готов!',
];

/** Длина `total` = число кадров `activePlayFrames`; на последнем кадре всегда реплика «ещё один клик». */
function buildRicePlayHints(total) {
  const H = RICE_GAME_SEQ_HINTS;
  const totalFrames = Math.max(0, Number(total) || 0);
  if (totalFrames <= 0) return [];
  if (totalFrames === 1) return [H[15] || 'Кликните ещё раз и подаем плов!'];

  // Подсказки для кадров прогресса (до финального кадра).
  // Нужно, чтобы на каждом клике подсказка менялась и не повторялась.
  const progress = H.slice(0, 15);
  const progressNeeded = Math.min(totalFrames - 1, progress.length);
  const selected = [...progress];

  // Если кадров меньше полного набора, в первую очередь выкидываем реплики 11–14 (индексы 10..13).
  let toRemove = Math.max(0, selected.length - progressNeeded);
  const removable = [13, 12, 11, 10];
  for (let i = 0; i < removable.length && toRemove > 0; i += 1) {
    const idx = removable[i];
    if (idx >= 0 && idx < selected.length) {
      selected.splice(idx, 1);
      toRemove -= 1;
    }
  }
  // Если всё ещё нужно укоротить (на очень коротких анимациях) — убираем равномерно из середины.
  while (toRemove > 0 && selected.length > progressNeeded) {
    const mid = Math.floor(selected.length / 2);
    selected.splice(mid, 1);
    toRemove -= 1;
  }

  const progressHints = selected.slice(0, progressNeeded);
  const lastFrameHint = H[15] || 'Кликните ещё раз и подаем плов!';
  return [...progressHints, lastFrameHint];
}

export default {
  name: 'RiceGamePage',
  components: { SiteHeader, Swiper, SwiperSlide },
  mixins: [metrikaMixin],
  inject: {
    shell: {
      from: appRootKey,
      default: null,
    },
  },
  data() {
    return {
      activeSection: 'interactive',
      navItems: [
        { id: 'hero', href: '/#hero', name: 'Главная' },
        { id: 'prizes', href: '/#prizes', name: 'Призы' },
        { id: 'interactive', href: '/#interactive', name: 'Интерактив' },
        { id: 'products', href: '/#products', name: 'Продукция' },
        { id: 'stores', href: '/#stores', name: 'Где купить' },
        { id: 'faq', href: '/#faq', name: 'Вопросы-ответы' },
      ],
      isMenuOpen: false,
      isHeaderScrolled: false,
      scrollThreshold: 40,
      scrollRafId: null,
      headerUser: null,
      wonRiceGames: [],
      wonRiceChainAccess: 'available',
      slotIndex: 0,
      screenPhase: 'loading',
      activeRiceGame: null,
      /** Индекс текущего кадра из `activePlayFrames` (десктоп / мобайл — см. пак). */
      frameStep: 0,
      riceHintText: '',
      riceGameError: '',
      riceGameFinalizing: false,
      riceIntroSwiperModules: [Pagination],
      /** Кадры `state_mobile_*` при ширине как у фона play (≤767px). */
      ricePlayUseMobileFrames: false,
      riceFrameMatchMedia: null,
      riceFrameMqlListener: null,
      riceHandTapOnEnd: null,
      /** Слот под полоску на экране результата: убираем после клика «Интересные факты», без скачка до этого. */
      riceResultProgressSlotDismissed: false,
    };
  },
  computed: {
    /** `rice_type` из final_round / won-rice (например «САМАРКАНД»). */
    resultRiceType() {
      const g = this.activeRiceGame;
      const raw =
        (g && (g.rice_type ?? g.riceType)) != null ? String(g.rice_type ?? g.riceType).trim() : '';
      return raw || 'Круглозерный';
    },
    riceIntroRuleSlides() {
      return [
        {
          key: 'r1',
          img1x: riceIntroRules1,
          imgSrcset: `${riceIntroRules1x2} 2x`,
          text: 'На экране пустой казан. Кликайте по казану и добавляйте ингредиенты.',
        },
        {
          key: 'r2',
          img1x: riceIntroRules2,
          imgSrcset: `${riceIntroRules2x2} 2x`,
          text: 'Продолжайте кликать до получения готового плова.',
        },
        {
          key: 'r3',
          img1x: riceIntroRules3,
          imgSrcset: `${riceIntroRules3x2} 2x`,
          text: 'За каждый открытый казан вы будете получать полезные факты о рисе и авторский рецепт.',
        },
        {
          key: 'r4',
          img1x: riceIntroRules4,
          imgSrcset: `${riceIntroRules4x2} 2x`,
          text: 'Соберите все 6 казанов и станьте участником розыгрыша приза — кухонной станции!',
        },
      ];
    },
    riceIntroRulesPagination() {
      return {
        el: '#rice-game-intro-rules-pagination',
        clickable: true,
        bulletClass: 'swiper-pagination-bullet rice-game-intro-rules-pagination__bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active rice-game-intro-rules-pagination__bullet--active',
      };
    },
    assetPack() {
      return resolveRiceGameAssetPack(this.activeRiceGame);
    },
    introPanelStyle() {
      const p = this.assetPack;
      return {
        '--rice-pack-bg-desktop': this.riceGamePackBgStyle(p.introBackgroundDesktop, p.introBackgroundDesktop2x),
        '--rice-pack-bg-mobile': this.riceGamePackBgStyle(p.introBackgroundMobile, p.introBackgroundMobile2x),
      };
    },
    playPanelStyle() {
      const p = this.assetPack;
      return {
        '--rice-pack-bg-desktop': this.riceGamePackBgStyle(p.backgroundDesktop, p.backgroundDesktop2x),
        '--rice-pack-bg-mobile': this.riceGamePackBgStyle(p.backgroundMobile, p.backgroundMobile2x),
      };
    },
    /** Последний кадр сцены — тот же вид, что на последнем шаге игры. */
    resultSceneFrameSrc() {
      const frames = this.activePlayFrames;
      if (!frames.length) return '';
      return frames[frames.length - 1] || '';
    },
    /** Текст под сценой на финале (вместо «ещё один клик»). */
    resultPanelHintText() {
      return RICE_GAME_SEQ_HINTS[16] || 'Плов готов!';
    },
    activePlayFrames() {
      const p = this.assetPack;
      if (this.ricePlayUseMobileFrames && p.framesMobile && p.framesMobile.length) {
        return p.framesMobile;
      }
      return p.frames;
    },
    currentFrameSrc() {
      const frames = this.activePlayFrames;
      if (!frames.length) return '';
      const i = Math.min(this.frameStep, frames.length - 1);
      return frames[i] || '';
    },
    frameProgressPercent() {
      const n = this.activePlayFrames.length;
      if (n <= 1) return 100;
      return (this.frameStep / (n - 1)) * 100;
    },
    /**
     * На последнем кадре полоска уже на 100%, а переход на результат — отдельный клик: индикатор скрываем.
     * При одном кадре полоску не показываем (тот же «финальный» шаг).
     */
    ricePlayProgressBarVisible() {
      const n = this.activePlayFrames.length;
      if (n <= 1) return false;
      return this.frameStep < n - 1;
    },
    wonRiceGamesNormalized() {
      return normalizeWonRiceGamesListForUi(this.wonRiceGames);
    },
    /**
     * Временный превью-режим: только `npm run serve` (NODE_ENV=development) и `?rice_preview=1`.
     * Опционально `rice_pack=domashniy|turkish|…` — форс визуального пака.
     */
    ricePreviewMode() {
      if (process.env.NODE_ENV !== 'development') return false;
      return String(this.$route.query.rice_preview || '').trim() === '1';
    },
    riceHeaderUploadDisabled() {
      return state.isUploadOpened;
    },
    /**
     * PATCH final_round отдаёт `next_available` — доступен ли следующий казан после этой победы
     * (не путать со статусом слота `available: 'next_available'` в won-rice).
     */
    riceFinalNextKazanDenied() {
      const g = this.activeRiceGame;
      if (!g) return false;
      const raw = g.finalRoundNextKazanStatus ?? g.next_available;
      if (raw == null || raw === '') return false;
      return String(raw).trim() === 'not_available';
    },
    /** «Следующий казан» на финале: в превью — просто следующий slot в URL. */
    riceResultAllowNextKazan() {
      if (this.ricePreviewMode) return this.slotIndex < 5;
      if (this.riceFinalNextKazanDenied) return false;
      return this.hasNextPlayableKazan;
    },
    resultTitle() {
      const t = this.activeRiceGame && this.activeRiceGame.title;
      return (t && String(t).trim()) || 'Рис для плова';
    },
    /** Финальный экран: ««Название плова»» готов!» */
    resultSubtitleSuccess() {
      return `Плов «${this.resultTitle}» готов!`;
    },
    /** Путь к PDF рецепта с бэка (как в ЛК: `receipt` и запасные ключи). */
    resultReceiptPath() {
      return resolveRiceRecipePdfPath(this.activeRiceGame);
    },
    hasResultReceipt() {
      return Boolean(this.resultReceiptPath);
    },
    /** Абсолютный URL PDF для `<a href>` (атрибут `download` с другого origin браузером часто игнорируется — см. `onResultReceiptPdfClick`). */
    resultReceiptPdfHref() {
      return riceReceiptAbsoluteUrl(this.resultReceiptPath);
    },
    resultReceiptDownloadName() {
      return riceRecipeSafeDownloadName(this.resultTitle, 'retsept');
    },
    resultDescription() {
      const d = this.activeRiceGame && this.activeRiceGame.description;
      if (d && String(d).trim()) return riceFormatFactBodyText(d);
      return 'Круглозёрный рис даёт плову мягкую, чуть маслянистую текстуру — как дома, в детстве.';
    },
    /** `title` из `final_round` — под заголовком пака слева (`factsBlockTitle`). */
    resultPackApiTitle() {
      const t = this.activeRiceGame && this.activeRiceGame.factsBlockTitle;
      if (t == null || !String(t).trim()) return '';
      return String(t).trim();
    },
    /** Картинка центра: `game_finished` из `final_round`, иначе `image`. */
    resultCenterPackImageUrl() {
      const g = this.activeRiceGame;
      if (!g) return '';
      const gf = g.game_finished != null ? String(g.game_finished).trim() : '';
      const raw =
        gf
          || (typeof g.image === 'string' && g.image.trim()
            ? g.image.trim()
            : '');
      if (!raw) return '';
      if (/^https?:\/\//i.test(raw)) return raw;
      const base = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
      return `${base}${raw.startsWith('/') ? '' : '/'}${raw}`;
    },
    /** Заголовок правой колонки (текст фактов — в `resultFactsBody`). */
    resultFactsHeading() {
      return 'А вы знали?';
    },
    /** Текст фактов из ответа `final_round` (`description`). */
    resultFactsBody() {
      const d = this.activeRiceGame && this.activeRiceGame.factsBlockDescription;
      if (d != null && String(d).trim()) return riceFormatFactBodyText(d);
      return this.resultDescription;
    },
    /** Опциональная ссылка из ответа `final_round` (`url`). */
    resultFactsUrl() {
      const u = this.activeRiceGame && this.activeRiceGame.factsBlockUrl;
      if (!u || typeof u !== 'string') return '';
      const p = u.trim();
      if (!p) return '';
      if (/^https?:\/\//i.test(p)) return p;
      const base = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
      return `${base}${p.startsWith('/') ? '' : '/'}${p}`;
    },
    resultImageUrl() {
      const img = this.activeRiceGame && this.activeRiceGame.image;
      if (!img || typeof img !== 'string') return '';
      const p = img.trim();
      if (/^https?:\/\//i.test(p)) return p;
      const base = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
      return `${base}${p.startsWith('/') ? '' : '/'}${p}`;
    },
    /** Первый слот после текущего, где можно играть (не заходим на закрытые). */
    nextPlayableSlotIndex() {
      if (state.promoStatus !== 'start' || this.wonRiceChainAccess !== 'available') return -1;
      const list = this.wonRiceGamesNormalized;
      for (let i = this.slotIndex + 1; i < list.length; i += 1) {
        if (list[i].available === 'next_available') {
          return i;
        }
      }
      return -1;
    },
    hasNextPlayableKazan() {
      return this.nextPlayableSlotIndex >= 0;
    },
    /** Следующий слот в списке (не обязательно доступен для игры). */
    nextSlotRow() {
      const list = this.wonRiceGamesNormalized;
      const j = this.slotIndex + 1;
      if (j >= list.length) return null;
      return list[j];
    },
    /** Следующий казан закрыт (`not_available`) — ведём на загрузку чека. */
    resultShowUploadCheckCta() {
      if (this.ricePreviewMode) return false;
      if (this.riceFinalNextKazanDenied) {
        return !!this.nextSlotRow;
      }
      if (this.hasNextPlayableKazan) return false;
      const row = this.nextSlotRow;
      return !!(row && row.available === 'not_available');
    },
    nextKazanBlockedHint() {
      if (this.hasNextPlayableKazan && !this.riceFinalNextKazanDenied) return '';
      if (this.ricePreviewMode && this.slotIndex >= 5) {
        return 'Превью: это последний казан (?slot=6). Уменьшите ?slot= в URL (1–6) или нажмите «Интро».';
      }
      const list = this.wonRiceGamesNormalized;
      const j = this.slotIndex + 1;
      if (this.riceFinalNextKazanDenied && j < list.length) {
        return 'Следующий казан пока закрыт. Загрузите чек в личном кабинете, чтобы открыть доступ.';
      }
      if (j < list.length && list[j].available === 'not_available') {
        return 'Следующий казан пока закрыт. Загрузите чек в личном кабинете, чтобы открыть доступ.';
      }
      return '';
    },
  },
  watch: {
    screenPhase(val) {
      if (val === 'result') {
        this.riceResultProgressSlotDismissed = false;
      }
    },
    '$route.fullPath'() {
      this.onRiceGameRouteChanged();
    },
  },
  mounted() {
    window.scrollTo(0, 0);
    window.addEventListener('scroll', this.onScrollThrottled, { passive: true });
    this.initRicePlayFrameMediaQuery();
    this.bootstrap();
  },
  beforeUnmount() {
    setMobileMenuScrollLock(false);
    window.removeEventListener('scroll', this.onScrollThrottled);
    const handEl = this.$refs.riceKazanHand;
    if (handEl && this.riceHandTapOnEnd) {
      handEl.removeEventListener('animationend', this.riceHandTapOnEnd);
      this.riceHandTapOnEnd = null;
    }
    if (this.riceFrameMatchMedia && this.riceFrameMqlListener) {
      this.riceFrameMatchMedia.removeEventListener('change', this.riceFrameMqlListener);
    }
    if (this.scrollRafId) {
      cancelAnimationFrame(this.scrollRafId);
      this.scrollRafId = null;
    }
  },
  methods: {
    popupErrorMessage(text) {
      return prefixPopupServerErrorMessage(text);
    },
    initRicePlayFrameMediaQuery() {
      if (typeof window === 'undefined' || !window.matchMedia) return;
      this.riceFrameMatchMedia = window.matchMedia('(max-width: 767px)');
      this.riceFrameMqlListener = () => {
        const next = this.riceFrameMatchMedia.matches;
        if (next === this.ricePlayUseMobileFrames) return;
        const prevLen = this.activePlayFrames.length;
        this.ricePlayUseMobileFrames = next;
        const nextLen = this.activePlayFrames.length;
        if (prevLen !== nextLen && this.frameStep >= nextLen) {
          this.frameStep = Math.max(0, nextLen - 1);
        }
      };
      this.riceFrameMatchMedia.addEventListener('change', this.riceFrameMqlListener);
      this.ricePlayUseMobileFrames = this.riceFrameMatchMedia.matches;
    },
    /** Фон панели: 1x + опциональный 2x (`image-set`). */
    riceGamePackBgStyle(url1x, url2x) {
      if (!url1x) return 'none';
      if (!url2x || url1x === url2x) {
        return `url(${url1x})`;
      }
      return `-webkit-image-set(url(${url1x}) 1x, url(${url2x}) 2x), image-set(url(${url1x}) 1x, url(${url2x}) 2x)`;
    },
    onScrollThrottled() {
      if (this.scrollRafId) return;
      this.scrollRafId = requestAnimationFrame(() => {
        this.scrollRafId = null;
        this.isHeaderScrolled = window.scrollY > this.scrollThreshold;
      });
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      setMobileMenuScrollLock(this.isMenuOpen);
    },
    closeMenu() {
      this.isMenuOpen = false;
      setMobileMenuScrollLock(false);
    },
    onRiceGameRouteChanged() {
      if (!this.$route.path.startsWith('/private/game/plov/')) return;
      this.riceGameError = '';
      this.syncSlotFromRoute();
    },
    /** Подмена title/rice_type под выбранный пак (только превью). */
    applyRicePreviewPackOverride(game) {
      if (!game || !this.ricePreviewMode) return game;
      const raw = String(this.$route.query.rice_pack || '').trim();
      if (!raw || !riceGamePacksById[raw]) return game;
      const pack = riceGamePacksById[raw];
      const label = (pack.matchTitles && pack.matchTitles[0]) || pack.id || raw;
      return {
        ...game,
        title: label,
        rice_type: label,
      };
    },
    ricePreviewGoIntro() {
      if (!this.ricePreviewMode) return;
      void this.$router.replace({
        path: `/private/game/plov/${this.slotIndex + 1}/intro`,
        query: this.riceGamePreserveQuery(),
      });
    },
    ricePreviewGoPlay() {
      if (!this.ricePreviewMode) return;
      if (!this.activeRiceGame) this.syncSlotFromRoute();
      void this.$router.replace({
        path: `/private/game/plov/${this.slotIndex + 1}/play`,
        query: this.riceGamePreserveQuery(),
      });
    },
    ricePreviewGoResult() {
      if (!this.ricePreviewMode) return;
      if (!this.activeRiceGame) this.syncSlotFromRoute();
      void this.$router.replace({
        path: `/private/game/plov/${this.slotIndex + 1}/result`,
        query: this.riceGamePreserveQuery(),
      });
    },
    redirectToHomePage() {
      this.$router.push('/');
    },
    redirectToPrivatePage() {
      this.$router.push('/private');
    },
    openCommonLoginPopup() {
      this.isMenuOpen = false;
      setMobileMenuScrollLock(false);
      if (!this.shell) {
        return;
      }
      this.shell.responseError = '';
      if (state.promoStatus == 'not-started' || !state.promoStatus || state.promoStatus == 'future') {
        this.shell.showPromoNotStarted = true;
        document.body.classList.add('is-popup-opened');
        return;
      }
      state.showCommonLoginPopup = true;
      this.shell.showCommonLoginPopup = true;
      document.body.classList.add('is-popup-opened');
      this.$nextTick(() => {
        if (this.shell.initFloatingLabels) {
          this.shell.initFloatingLabels();
        }
      });
    },
    handleHeaderNavClick(sectionId) {
      this.$sendMetrik('reachGoal', 'click_nav');
      const cleanSectionId = String(sectionId || '').replace(/^#/, '');
      const accountSections = ['account-info', 'account-checks', 'account-interactive', 'account-prizes'];
      if (accountSections.includes(cleanSectionId)) {
        this.$router.push(`/private#${cleanSectionId}`);
        return;
      }
      if (this.$route.path !== '/') {
        this.$router.push(`/#${cleanSectionId}`);
      }
    },
    async logout() {
      removeToken();
      this.headerUser = null;
      this.isMenuOpen = false;
      setMobileMenuScrollLock(false);
      window.location.href = '/';
    },
    applyHeaderUser(userInfo) {
      if (!userInfo) {
        this.headerUser = null;
        return;
      }
      this.headerUser = {
        first_name: userInfo.first_name,
        last_name_initial: (userInfo.last_name || '').charAt(0),
      };
    },
    async bootstrap() {
      this.screenPhase = 'loading';
      if (!getToken()) {
        this.$router.replace('/');
        return;
      }
      setAuthHeader();
      try {
        const userInfo = await getUserInfo();
        this.applyHeaderUser(userInfo);
        await this.loadWonRiceGames();
        this.syncSlotFromRoute();
      } catch (e) {
        console.error(e);
        this.screenPhase = 'unavailable';
      }
    },
    async loadWonRiceGames() {
      if (!getToken()) return;
      try {
        const data = await fetchWonRiceGames();
        const { items, chainAccess } = parseWonRiceGamesResponse(data);
        this.wonRiceChainAccess = chainAccess;
        const base = Array.isArray(items) ? items : [];
        this.wonRiceGames = mergePendingIntoWonRiceList(base);
      } catch (e) {
        console.error(e);
        this.wonRiceGames = [];
        this.wonRiceChainAccess = 'available';
      }
    },
    riceGamePreserveQuery() {
      const q = { ...this.$route.query };
      delete q.slot;
      delete q.phase;
      return q;
    },
    parseRiceRouteSlot1() {
      const n = parseInt(String(this.$route.params.slot || '1'), 10);
      return Number.isFinite(n) && n >= 1 && n <= 6 ? n : 1;
    },
    parseRiceRoutePhase(slot1) {
      const rawPh = String(this.$route.params.phase || 'intro').toLowerCase();
      const ok = ['intro', 'play', 'result'].includes(rawPh);
      if (!ok && this.$route.params.phase) {
        void this.$router.replace({
          path: `/private/game/plov/${slot1}/intro`,
          query: this.riceGamePreserveQuery(),
        });
      }
      return ok ? rawPh : 'intro';
    },
    applyWonRowToActiveGame(row) {
      const raw = row.__raw || {};
      const prev = this.activeRiceGame && typeof this.activeRiceGame === 'object' ? this.activeRiceGame : {};
      const prevFactsTitle = prev.factsBlockTitle != null && String(prev.factsBlockTitle).trim()
        ? String(prev.factsBlockTitle).trim()
        : '';
      const prevFactsDesc = prev.factsBlockDescription != null && String(prev.factsBlockDescription).trim()
        ? String(prev.factsBlockDescription).trim()
        : '';
      const prevFactsUrl = prev.factsBlockUrl != null && String(prev.factsBlockUrl).trim()
        ? String(prev.factsBlockUrl).trim()
        : '';
      const prevReceipt = prev.receipt != null && String(prev.receipt).trim()
        ? String(prev.receipt).trim()
        : '';
      const prevGameFinished = prev.game_finished != null && String(prev.game_finished).trim()
        ? String(prev.game_finished).trim()
        : '';
      this.activeRiceGame = {
        ...raw,
        __raw: raw,
        title: row.title || raw.title,
        receipt: (row.receipt && String(row.receipt).trim()) || raw.receipt || prevReceipt || '',
        image: (row.image && String(row.image).trim()) || raw.image || prev.image || '',
        description: raw.description != null ? String(raw.description) : '',
        factsBlockTitle:
          (raw.facts_block_title != null && String(raw.facts_block_title).trim())
            ? String(raw.facts_block_title).trim()
            : ((raw.factsBlockTitle != null && String(raw.factsBlockTitle).trim())
              ? String(raw.factsBlockTitle).trim()
              : ((raw.facts_block_name != null && String(raw.facts_block_name).trim())
                ? String(raw.facts_block_name).trim()
                : (prevFactsTitle || ''))),
        factsBlockDescription:
          (raw.facts_block_description != null && String(raw.facts_block_description).trim())
            ? String(raw.facts_block_description).trim()
            : ((raw.factsBlockDescription != null && String(raw.factsBlockDescription).trim())
              ? String(raw.factsBlockDescription).trim()
              : ((raw.description != null && String(raw.description).trim())
                ? String(raw.description).trim()
                : (prevFactsDesc || (raw.description != null ? String(raw.description) : '')))),
        factsBlockUrl: typeof raw.facts_block_url === 'string' && raw.facts_block_url.trim()
          ? raw.facts_block_url.trim()
          : (typeof raw.factsBlockUrl === 'string' && raw.factsBlockUrl.trim()
            ? raw.factsBlockUrl.trim()
            : (typeof raw.url === 'string' && raw.url.trim()
              ? raw.url.trim()
              : (prevFactsUrl || ''))),
        game_finished: raw.game_finished != null && String(raw.game_finished).trim()
          ? String(raw.game_finished).trim()
          : (prevGameFinished || ''),
        available: 'won',
        finalRoundNextKazanStatus:
          raw.next_available != null && String(raw.next_available).trim() !== ''
            ? String(raw.next_available).trim()
            : undefined,
      };
    },
    setActiveRiceGameFromNextAvailableRow(row) {
      const raw = row.__raw || {};
      this.activeRiceGame = {
        ...raw,
        title: row.title || raw.title,
        receipt: row.receipt || raw.receipt,
        image: row.image || raw.image,
        available: row.available,
      };
    },
    syncRiceRoutePathIfNeeded(slot1, phase) {
      const nextPath = `/private/game/plov/${slot1}/${phase}`;
      if (this.$route.path !== nextPath) {
        void this.$router.replace({ path: nextPath, query: this.riceGamePreserveQuery() });
      }
    },
    syncSlotFromRoute() {
      const slot1 = this.parseRiceRouteSlot1();
      let phase = this.parseRiceRoutePhase(slot1);
      this.slotIndex = slot1 - 1;
      const row = this.wonRiceGamesNormalized[this.slotIndex];

      if (this.ricePreviewMode) {
        let base;
        if (row) {
          const raw = row.__raw || {};
          base = {
            ...raw,
            title: row.title || raw.title || `Казан ${this.slotIndex + 1}`,
            receipt: row.receipt || raw.receipt || '',
            image: row.image || raw.image || '',
            description: row.description || raw.description || '',
            available: row.available,
          };
        } else {
          base = {
            title: `Превью · слот ${this.slotIndex + 1}`,
            description: 'Режим превью: данные с бэка необязательны.',
            image: '',
            receipt: '',
            available: 'next_available',
          };
        }
        if (phase !== 'result' || !this.activeRiceGame) {
          this.activeRiceGame = this.applyRicePreviewPackOverride(base);
        } else {
          this.activeRiceGame = this.applyRicePreviewPackOverride({
            ...base,
            ...this.activeRiceGame,
            title: this.activeRiceGame.title || base.title,
          });
        }
        const fl = this.activePlayFrames.length;
        if (phase === 'result') {
          this.screenPhase = 'result';
          this.frameStep = fl > 0 ? fl - 1 : 0;
        } else if (phase === 'play') {
          this.screenPhase = 'play';
          this.frameStep = 0;
          this.riceHintText = this.hintForFrame(0, Math.max(fl, 1));
        } else {
          this.screenPhase = 'intro';
          this.frameStep = 0;
        }
        return;
      }

      if (!row || row.available === 'not_available') {
        this.screenPhase = 'unavailable';
        this.activeRiceGame = null;
        return;
      }

      if (row.available === 'won') {
        if (phase !== 'result') {
          phase = 'result';
          this.syncRiceRoutePathIfNeeded(slot1, 'result');
        }
        this.applyWonRowToActiveGame(row);
        const fl = this.activePlayFrames.length;
        this.frameStep = fl > 0 ? fl - 1 : 0;
        this.screenPhase = 'result';
        return;
      }

      if (row.available === 'next_available') {
        if (phase === 'result') {
          phase = 'intro';
          this.syncRiceRoutePathIfNeeded(slot1, 'intro');
        }
        if (state.promoStatus !== 'start' || this.wonRiceChainAccess !== 'available') {
          this.screenPhase = 'unavailable';
          this.activeRiceGame = null;
          return;
        }
        this.setActiveRiceGameFromNextAvailableRow(row);
        const fl = this.activePlayFrames.length;
        if (phase === 'play') {
          this.screenPhase = 'play';
          this.frameStep = 0;
          this.riceHintText = this.hintForFrame(0, Math.max(fl, 1));
        } else {
          this.screenPhase = 'intro';
          this.frameStep = 0;
        }
        return;
      }

      this.screenPhase = 'unavailable';
      this.activeRiceGame = null;
    },
    async startCook() {
      this.riceGameError = '';
      this.riceGameFinalizing = true;
      try {
        if (this.ricePreviewMode) {
          await this.loadWonRiceGames();
          const row = this.wonRiceGamesNormalized[this.slotIndex];
          if (row) {
            const raw = row.__raw || {};
            this.activeRiceGame = this.applyRicePreviewPackOverride({
              ...raw,
              title: row.title || raw.title || `Казан ${this.slotIndex + 1}`,
              receipt: row.receipt || raw.receipt || '',
              image: row.image || raw.image || '',
              description: row.description || raw.description || '',
              available: row.available,
            });
          } else if (!this.activeRiceGame) {
            this.syncSlotFromRoute();
          }
          await this.$router.replace({
            path: `/private/game/plov/${this.slotIndex + 1}/play`,
            query: this.riceGamePreserveQuery(),
          });
          return;
        }

        await this.loadWonRiceGames();
        const row = this.wonRiceGamesNormalized[this.slotIndex];
        if (!row || row.available !== 'next_available') {
          this.riceGameError =
            'Этот казан сейчас недоступен. Обновите страницу или откройте игру снова из личного кабинета.';
          return;
        }
        if (this.wonRiceChainAccess !== 'available') {
          this.riceGameError =
            this.wonRiceChainAccess === 'all_won'
              ? 'Все призы выиграны.'
              : 'Следующий казан недоступен. Проверьте условия в личном кабинете.';
          return;
        }
        if (state.promoStatus !== 'start') {
          this.riceGameError = 'Игра доступна только на время акции.';
          return;
        }
        const raw = row.__raw || {};
        this.activeRiceGame = {
          ...raw,
          title: row.title || raw.title,
          receipt: row.receipt || raw.receipt,
          image: row.image || raw.image,
          available: row.available,
        };
        await startGameSession(raw, this.slotIndex);
        await this.$router.replace({
          path: `/private/game/plov/${this.slotIndex + 1}/play`,
          query: this.riceGamePreserveQuery(),
        });
      } catch (e) {
        const data = e?.response?.data;
        this.riceGameError = data ? formatFeedbackSendError(data) : 'Игровые сессии не доступны';
      } finally {
        this.riceGameFinalizing = false;
      }
    },
    hintForFrame(step, total) {
      if (total <= 1) {
        return 'Кликните по казану, чтобы завершить игру.';
      }
      const hints = buildRicePlayHints(total);
      const i = Math.min(Math.max(0, step), hints.length - 1);
      return hints[i] || '';
    },
    triggerKazanHandTapAnimation() {
      this.$nextTick(() => {
        const el = this.$refs.riceKazanHand;
        if (!el || typeof el.classList === 'undefined') return;
        if (this.riceHandTapOnEnd) {
          el.removeEventListener('animationend', this.riceHandTapOnEnd);
          this.riceHandTapOnEnd = null;
        }
        el.classList.remove('rice-game-kazan__hand--tap');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.classList.add('rice-game-kazan__hand--tap');
            const onEnd = (e) => {
              if (e.animationName !== 'rice-hand-tap') return;
              el.classList.remove('rice-game-kazan__hand--tap');
              el.removeEventListener('animationend', onEnd);
              if (this.riceHandTapOnEnd === onEnd) {
                this.riceHandTapOnEnd = null;
              }
            };
            this.riceHandTapOnEnd = onEnd;
            el.addEventListener('animationend', onEnd);
          });
        });
      });
    },
    async onKazanClick() {
      if (this.riceGameFinalizing) return;
      const frames = this.activePlayFrames;
      const len = frames.length;
      if (!len) return;

      this.triggerKazanHandTapAnimation();

      if (this.frameStep < len - 1) {
        this.frameStep += 1;
        this.riceHintText = this.hintForFrame(this.frameStep, len);
        return;
      }

      this.riceGameFinalizing = true;
      let finishedOk = false;
      try {
        if (this.ricePreviewMode) {
          finishedOk = true;
        } else {
          const data = await completeGameFinalRound();
          if (data && typeof data === 'object' && this.activeRiceGame) {
            const packTitle = this.activeRiceGame.title;
            const apiTitle = data.title != null ? String(data.title).trim() : '';
            const apiDesc = data.description != null ? String(data.description).trim() : '';
            this.activeRiceGame = {
              ...this.activeRiceGame,
              ...data,
              /* Название плова в шапке / «… готов!» / левая колонка — из слота, не подменяем заголовком фактов с бэка. */
              title: packTitle,
              factsBlockTitle: apiTitle,
              factsBlockDescription: apiDesc,
              factsBlockUrl: typeof data.url === 'string' ? data.url.trim() : '',
              game_finished:
                data.game_finished != null && String(data.game_finished).trim()
                  ? String(data.game_finished).trim()
                  : (this.activeRiceGame.game_finished || ''),
              receipt:
                data.receipt
                ?? data.recipe_pdf
                ?? data.recipe_url
                ?? this.activeRiceGame.receipt,
              image: data.image ?? this.activeRiceGame.image,
              description: apiDesc || this.activeRiceGame.description,
              finalRoundNextKazanStatus:
                data.next_available != null && String(data.next_available).trim() !== ''
                  ? String(data.next_available).trim()
                  : undefined,
            };
            const kazanRowId = this.activeRiceGame?.id;
            setRiceGamePending({
              savedAt: Date.now(),
              slotIndex: this.slotIndex,
              kazanId: kazanRowId != null && kazanRowId !== '' ? kazanRowId : undefined,
              matchTitle: String(packTitle || '').trim(),
              apiId: kazanRowId != null && kazanRowId !== '' ? kazanRowId : data.id,
              title: packTitle,
              rice_type:
                data.rice_type != null && String(data.rice_type).trim()
                  ? String(data.rice_type).trim()
                  : (data.riceType != null && String(data.riceType).trim() ? String(data.riceType).trim() : undefined),
              receipt: data.receipt ?? data.recipe_pdf ?? data.recipe_url,
              image: data.image,
              description: apiDesc || undefined,
              factsBlockTitle: apiTitle || undefined,
              factsBlockDescription: apiDesc || undefined,
              factsBlockUrl: typeof data.url === 'string' && data.url.trim() ? data.url.trim() : undefined,
              game_finished:
                data.game_finished != null && String(data.game_finished).trim()
                  ? String(data.game_finished).trim()
                  : undefined,
            });
            await this.loadWonRiceGames();
            finishedOk = true;
          }
        }
        if (finishedOk) {
          await this.$router.replace({
            path: `/private/game/plov/${this.slotIndex + 1}/result`,
            query: this.riceGamePreserveQuery(),
          });
        }
      } catch (e) {
        console.error('completeGameFinalRound', e);
      } finally {
        this.riceGameFinalizing = false;
      }
    },
    async onResultReceiptPdfClick() {
      const url = this.resultReceiptPdfHref;
      if (!url) return;
      await downloadRiceRecipePdf(url, this.resultTitle);
    },
    /** Как в ЛК: чек / QR или страница загрузки в Telegram. */
    goUploadCheckForNextKazan() {
      this.$sendMetrik('reachGoal', 'Check_Click');
      if (this.$route?.meta?.telegramPageType === 'profile') {
        adRiver.trackRegisterReceiptClick();
        this.$router.push('/telegram_bot_upload_receipt_page');
        return;
      }
      adRiver.trackRegisterReceiptClick();
      if (typeof _txq !== 'undefined') {
        _txq.push(['track', 'click_upload_check_after_sign_in']);
      }
      if (state.promoStatus === 'start') {
        const qrElement = document.getElementById('apm-scan-qr');
        if (qrElement) {
          state.isUploadOpened = true;
          document.body.classList.add('is-upload-widget-opened');
          qrElement.click();
          return;
        }
      }
      void this.$router.push({ path: '/private', hash: '#account-checks' });
      if (state.promoStatus === 'not-started' && this.shell) {
        this.shell.showPromoNotStarted = true;
      } else if (state.promoStatus === 'end' && this.shell) {
        this.shell.showPromoEnd = true;
      }
    },
    scrollToResultFacts() {
      this.riceResultProgressSlotDismissed = true;
      /* Сначала убирается резерв под полосу — без отложенного скролла картинка «прыгает» и первый scrollIntoView резает блок. */
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const el = this.$refs.riceGameResultFacts;
            if (!el) return;
            el.focus({ preventScroll: true });
            const header = document.querySelector('header');
            const gap = 16;
            const offset = header && header.offsetHeight ? header.offsetHeight + gap : 120;
            const y = el.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
          });
        });
      });
    },
    async goProfile() {
      await this.loadWonRiceGames();
      this.$router.push({ path: '/private', hash: '#account-interactive' });
    },
    async goNextKazan() {
      if (!this.ricePreviewMode && this.riceFinalNextKazanDenied) {
        this.goUploadCheckForNextKazan();
        return;
      }
      await this.loadWonRiceGames();
      const nextIdx = this.ricePreviewMode ? this.slotIndex + 1 : this.nextPlayableSlotIndex;
      if (nextIdx < 0 || (this.ricePreviewMode && nextIdx > 5)) {
        await this.goProfile();
        return;
      }
      await this.$router.replace({
        path: `/private/game/plov/${nextIdx + 1}/intro`,
        query: this.riceGamePreserveQuery(),
      });
    },
  },
};
</script>
